/**
 * The board's spine.
 *
 * It used to be our own model export, which is 431 players. That is a fine
 * list to *value* from and a bad one to draft from: it ends around the
 * fourteenth round of a twelve-team league, and it is missing two thirds of
 * the defenses in the consensus top 250, which is exactly where a real draft
 * is when someone finally takes one.
 *
 * So the rows come from the expert consensus — the longest, most complete list
 * we have, and the one the board is ordered by out of the box — and the model
 * export folds onto it for the numbers only the model has: the projection, the
 * value over replacement that prices an auction, the floor and the ceiling.
 *
 * A player the model carries and the consensus does not stays on the board
 * rather than being dropped. That case is rare and it is exactly the case
 * worth keeping: somebody the experts have not gotten to yet.
 */

import { indexPlayers, boardRowId, canonicalTeam } from './playerMatch.js';
import { buildBoardFromModelValues } from './modelValues.js';

/**
 * A row for a consensus player the model does not project.
 *
 * Every model-derived field is null rather than zero. A zero projection is a
 * claim that he will score nothing; null is the truth, which is that we have
 * no projection — the model needs a prior NFL season, and rookies, kickers and
 * defenses do not have one it can read.
 */
const consensusOnlyRow = (player) => ({
  id: boardRowId(player.name, player.position, player.team),
  name: player.name,
  position: player.position,
  team: canonicalTeam(player.team) || 'FA',
  proj: null,
  expected: null,
  usageExpected: null,
  games: null,
  floor: null,
  ceiling: null,
  spread: null,
  adp: null,
  adpStdev: null,
  adpRank: null,
  marketProps: null,
  marketPts: null,
  marketEdge: null,
  bye: player.bye ?? null,
  confidence: null,
  modelEdge: null,
  flags: [],
  attribution: null,
  isPick: false,
});

/**
 * Consensus rows first, in consensus order, then whatever the model carries
 * that the consensus does not.
 *
 * The ranking that actually orders the board is applied later and can be any
 * of three things; this only decides who is on it.
 */
export const buildConsensusBoard = ({ ecrPlayers, modelPayload, scoringSettings }) => {
  const modelRows = modelPayload
    ? buildBoardFromModelValues(modelPayload, scoringSettings)
    : [];

  // Without a consensus feed the model export is the whole board, exactly as
  // it was before — a failed fetch costs columns, never the board.
  if (!ecrPlayers?.length) return modelRows;

  const lookup = indexPlayers(modelRows);
  const claimed = new Set();
  const rows = [];
  // Two rows sharing an id would fight over the same watchlist star and the
  // same drafted flag, so the first — best-ranked — one wins outright.
  const seen = new Set();

  const push = (row) => {
    if (seen.has(row.id)) return;
    seen.add(row.id);
    rows.push(row);
  };

  for (const player of ecrPlayers) {
    const model = lookup(player);
    if (model) claimed.add(model.id);

    const base = model || consensusOnlyRow(player);
    // The consensus feed is refreshed on its own and is the fresher of the two
    // on identity: it has the trade that happened on Tuesday, and a model
    // export from last week does not.
    const name = player.name || base.name;
    const team = canonicalTeam(player.team) || base.team || 'FA';
    push({
      ...base,
      name,
      team,
      id: boardRowId(name, base.position, team),
      bye: player.bye ?? base.bye ?? null,
    });
  }

  for (const row of modelRows) {
    if (!claimed.has(row.id)) push(row);
  }

  return rows;
};
